import { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { map } from 'lodash';

import { defaultFlags } from '@/providers/FeatureFlagsProvider/defaultFlags';
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider/hooks/useFeatureFlag';
import { useFeatureFlagLocalOverrides } from '@/providers/FeatureFlagsProvider/hooks/useFeatureFlagLocalOverrides';

import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import {
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
  styled,
} from '@mui/joy';
import Button from '@mui/joy/Button';

const FeatureFlagSwitcherContainer = styled(Stack)`
  position: absolute;
  bottom: 0;
  right: 0;
  background-color: #d4d6dd;
  z-index: 10000;
  border-top-left-radius: 8px;
  padding: 4px;
  gap: 8px;
  height: 300px;
  width: 320px;
  transform: translate(276px, 256px);
  transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);

  &.open {
    transform: translate(0, 0);
    opacity: 1;
  }
`;

export const FeatureFlagSwitcher = () => {
  const [toggle, setToggle] = useState(false);
  const { setOverrides, overrides } = useFeatureFlagLocalOverrides();

  const setOverrideHandler = useCallback(
    (flagName: string, value: boolean) => {
      setOverrides({
        ...overrides,
        [flagName]: value,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setOverrides],
  );

  const isFeatureFlagEnabled = useFeatureFlag('featureFlagsSwitcher');

  const flags = useMemo(() => ({ ...defaultFlags, ...overrides }), [overrides]);

  return (
    isFeatureFlagEnabled && (
      <FeatureFlagSwitcherContainer className={toggle ? 'open' : ''}>
        <Stack flexDirection='row' alignItems='center' gap={1}>
          <Tooltip
            title={`Feature Flags / ${toggle ? 'Close' : 'Open'}`}
            placement='left'
          >
            <IconButton
              onClick={() => setToggle(!toggle)}
              sx={{ width: 32, height: 32 }}
            >
              <FeaturedPlayListIcon />
            </IconButton>
          </Tooltip>
          <Typography sx={{ color: '#616778' }} level='body-lg'>
            Feature Flags
          </Typography>
        </Stack>
        <Stack
          gap={2}
          sx={{ padding: '0 8px', paddingBottom: '8px', height: '100%' }}
        >
          {map(flags, (value, flagName) => (
            <Typography
              key={flagName}
              component='label'
              sx={{ color: '#616778', cursor: 'pointer' }}
              startDecorator={
                <Switch
                  sx={{ ml: 1 }}
                  checked={value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setOverrideHandler(flagName, e.target.checked)
                  }
                />
              }
            >
              {flagName}
            </Typography>
          ))}
          <Button
            onClick={() => window.location.reload()}
            sx={{ marginTop: 'auto' }}
          >
            Activate Features
          </Button>
        </Stack>
      </FeatureFlagSwitcherContainer>
    )
  );
};
